<template>
  <div class="report-competencies">
    <h3>Компетентностный профиль руководителя проекта</h3>
    <div v-if="errors.getError('projectId')" class="set_id_error">
      {{ errors.getError('projectId') }}
    </div>

    <a-row class="report-competencies__grid">
      <template v-for="(item, index) in competenceSetList" :key="index">
        <project-competence-card
          :index="index"
          :competence-set="item"
          :api-errors="getArrayApiErrors(index)"
          :class="{ active: isActive }"
          @click.stop="handleClick(index)"
        />
      </template>
    </a-row>
  </div>
</template>

<script setup>
  import { computed, onMounted, ref } from 'vue';
  import { useRoute } from 'vue-router';
  import { useApi } from '@/api/use-api';
  import ProjectCompetenceCard from '@/components/projects/management/Competencies/ProjectCompetenceCard.vue';
  import { PROJECT_STATUS_ACCEPTANCE } from '@/api/models/ProjectStatus';
  import {
    PROJECT_COMPETENCE_SET_STATUS_CONFIRMED,
    PROJECT_COMPETENCE_SET_STATUS_DEFAULT,
  } from '@/constants/project-competence-set';
  import { useArrayErrors } from '@/composables/useArrayErrors';
  import ErrorsHandler from '@/components/form/errors-handler';

  const api = useApi();
  const route = useRoute();
  const emit = defineEmits(['saved']);
  const isLoading = ref(true);
  const competenceSetList = ref([]);

  const props = defineProps({
    projectStatusId: Number,
    isMyProject: {
      type: Boolean,
      default: false,
    },
    /** True if the project status type is 'to_approve' */
    toApprove: {
      type: Boolean,
      default: false,
    },
  });

  const errors = new ErrorsHandler({
    projectId: '',
    competenceSetList: '',
  });

  const {
    handleArrayErrors,
    getArrayApiErrors,
    clearArrayError,
    resetArrayErrors,
  } = useArrayErrors('competenceSetList', competenceSetList);

  onMounted(async () => {
    await getCompetenceSetList();
    isLoading.value = false;
  });

  const isActive = computed(() => {
    return (
      props.projectStatusId === PROJECT_STATUS_ACCEPTANCE &&
      props.isMyProject &&
      props.toApprove
    );
  });

  const getCompetenceSetList = async () => {
    try {
      const response = await api.projectCompetenceSet.index(
        new URLSearchParams({
          projectId: route.params.id,
        })
      );
      competenceSetList.value = response.sets.items.map((item, index) => ({
        ...item,
        index: index,
      }));
    } catch (error) {
      console.error('error', error);
    }
  };

  const handleClick = (index) => {
    if (!isActive.value) return;

    errors.clearErrors();
    clearArrayError(index);

    const item = competenceSetList.value[index];
    if (item.confirmedAt || item.rejectedAt) {
      item.confirmedAt = null;
      item.rejectedAt = null;
      item.status = PROJECT_COMPETENCE_SET_STATUS_DEFAULT;
    } else {
      item.confirmedAt = Math.floor(new Date().getTime() / 1000);
      item.rejectedAt = null;
      item.status = PROJECT_COMPETENCE_SET_STATUS_CONFIRMED;
    }
  };

  const saveCompetencies = async () => {
    errors.clearErrors();
    resetArrayErrors();

    try {
      await api.projectCompetenceSet.confirmSetList({
        projectId: route.params.id,
        competenceSetList: prepareSetList(),
      });

      emit('saved', true);
      await getCompetenceSetList();
    } catch (error) {
      errors.handleApiErrors(error);
      handleArrayErrors(error);
      emit('saved', false);
    } finally {
      isLoading.value = false;
    }
  };

  const prepareSetList = () => {
    return competenceSetList.value.map((el) => {
      return {
        id: el.id,
        projectId: el.projectId,
        confirmedAt: el.confirmedAt,
        rejectedAt: el.confirmedAt
          ? null
          : el.rejectedAt ?? Math.floor(new Date().getTime() / 1000),
      };
    });
  };

  defineExpose({ saveCompetencies });
</script>

<style lang="scss" scoped>
  @use "@/assets/styles/_colors.scss" as *;

  * {
    font-family: 'Nunito Sans', sans-serif;
  }

  .report-competencies {
    margin-bottom: 30px;
    max-width: 902px;

    h3 {
      font-size: 18px;
      margin-bottom: 10px;
    }

    .set_id_error {
      color: $color-text-accent-red;
      margin-bottom: 14px;
    }

    .active {
      cursor: pointer;
    }

    &__grid {
      padding: 20px 0;
      column-gap: 20px;
      row-gap: 20px;

      .empty_card {
        align-items: center;
        background-color: $color-bg-primary-white;
        border: 2px dashed $color-input;
        border-radius: 4px;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 9px;
        max-height: 303px;
        max-width: 210px;
        user-select: none;
        height: 303px;
        width: 210px;

        .avatar {
          &__wrapper {
            align-items: center;
            background-color: $color-bg-primary-white;
            border: 2px dashed $color-input;
            border-radius: 50%;
            display: flex;
            justify-content: center;
            position: relative;
            height: 80px;
            width: 80px;

            img {
              filter: invert(82%) sepia(2%) saturate(4563%) hue-rotate(197deg)
                brightness(89%) contrast(93%);
              margin: auto;
              height: 21px;
              width: 21px;
            }

            .skeleton {
              width: auto;
            }
          }
        }

        .label {
          color: $color-bg-status-new;
          font-weight: 400;
          font-size: 14px;
          line-height: 19.1px;
          padding: 25px;
        }
      }
    }
  }
</style>
